import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { StyledIcon, SvgSrc } from '~/components/shared/Icon/Icon';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import { isValid } from 'swedish-personal-identity-number-validator';
import InputField from '~/components/shared/Input/InputField';
import DropDown from '~/components/shared/DropDown/DropDown';
import { OwnerAmountEnum } from '~/enums';
import { validateEmail } from '@qred/shared-component-library/src/validators';
import { IOwner } from '~/Interfaces';
import useTranslate from '~/hooks/useTranslate';
import { DoneButton } from '../PaymentConfirmation';
import { TryAgainContainer } from '../../SomethingWentWrong';
import { ErrorMessage, ErrorMessageIcon } from '../Instalments/InstalmentCards';

const OwnerInputField = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 310px;
  margin: auto;
`;

const ErrorMessageUBO = styled((props) => <ErrorMessage {...props} />)`
  padding-top: 0px;
  padding-bottom: 20px;
  padding-left: 0px;
`;

const ErrorMessageText = styled.div`
  font-size: 16px;
  padding-left: 16px;
  padding-top: 4px;
  @media (max-width: 688px) {
    padding-top: 0px;
    padding-bottom: 8px;
    padding-left: 10px;
  }
`;
interface Props {
  onSave: (owner?: IOwner, index?: number) => void;
  owner?: IOwner;
  index?: number;
}

const Owner: React.FC<Props> = (props) => {
  const { onSave, owner, index } = props;

  const [personalNumber, setPersonalNumber] = useState<string>('');
  const [personalNumberError, setPersonalNumberError] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [amountError, setAmountError] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');

  const translate = useTranslate();

  useEffect(() => {
    if (owner) {
      setPersonalNumber(owner.personalNumber);
      setAmount(owner.amount);
      setName(owner.name);
      setEmail(owner.email ? owner.email : '');
    } else {
      setAmount(OwnerAmountEnum.TwentyFiveTo50);
    }
  }, [owner]);

  const onPersonalNumberChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPersonalNumberError('');
    setPersonalNumber(e.target.value);
  };
  const onNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameError('');
    setName(e.target.value);
  };
  const onAmountChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setAmountError('');
    setAmount(event.currentTarget.value);
  };
  const onEmailChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailError('');
    setEmail(e.target.value);
  };
  const saveOwner = () => {
    if (personalNumber === '') {
      setPersonalNumberError(
        translate('Invoices.PersonalNumberError').toString()
      );
      return;
    }
    if (!isValid(personalNumber)) {
      setPersonalNumberError(
        translate('Invoices.PersonalNumberInvalidFormat').toString()
      );
      return;
    }
    if (name === '') {
      setNameError(translate('Invoices.OwnersNameError').toString());
      return;
    }
    if (email === '') {
      setEmailError(translate('Invoices.OwnersEmailError').toString());
      return;
    }
    if (!validateEmail(email)) {
      setEmailError(translate('Invoices.OwnersEmailInvalidFormat').toString());
      return;
    }
    if (amount === '') {
      setAmountError(translate('Invoices.AddOwnershipPercentage').toString());
    }

    setPersonalNumberError('');
    setNameError('');
    setAmountError('');
    setEmailError('');

    const editedOwner: IOwner = {
      personalNumber,
      name,
      amount,
      email: owner ? owner.email : email,
    };

    onSave(editedOwner, index);
  };

  return (
    <div>
      <OwnerInputField>
        <Typography style={{ fontSize: '16px' }} fontWeight={FontWeights.Bold}>
          {' '}
          {translate('PersonalNumber')}{' '}
        </Typography>
        <InputField
          type="text"
          name="inputPersonalNumber"
          placeholder="YYYYMMDD-XXXX"
          disabled={false}
          value={personalNumber}
          onChange={onPersonalNumberChanged}
        />
        {personalNumberError && (
          <ErrorMessageUBO>
            <ErrorMessageIcon>
              <StyledIcon color="#EB4C6A" icon={SvgSrc.WarningCircle} />
            </ErrorMessageIcon>
            <ErrorMessageText>{personalNumberError}</ErrorMessageText>
          </ErrorMessageUBO>
        )}
      </OwnerInputField>
      <OwnerInputField>
        <Typography style={{ fontSize: '16px' }} fontWeight={FontWeights.Bold}>
          {' '}
          {translate('Name')}{' '}
        </Typography>
        <InputField
          type="text"
          name="inputFullName"
          placeholder={translate('FullName').toString()}
          disabled={false}
          value={name}
          onChange={onNameChanged}
        />
        {nameError && (
          <ErrorMessageUBO>
            <ErrorMessageIcon>
              <StyledIcon color="#EB4C6A" icon={SvgSrc.WarningCircle} />
            </ErrorMessageIcon>
            <ErrorMessageText>{nameError}</ErrorMessageText>
          </ErrorMessageUBO>
        )}
      </OwnerInputField>
      <OwnerInputField>
        <Typography style={{ fontSize: '16px' }} fontWeight={FontWeights.Bold}>
          {' '}
          {translate('Email')}{' '}
        </Typography>
        <InputField
          type="text"
          name="inputEmail"
          placeholder="thomas@qred.com"
          disabled={false}
          value={email}
          onChange={onEmailChanged}
        />
        {emailError && (
          <ErrorMessageUBO>
            <ErrorMessageIcon>
              <StyledIcon color="#EB4C6A" icon={SvgSrc.WarningCircle} />
            </ErrorMessageIcon>
            <ErrorMessageText>{emailError}</ErrorMessageText>
          </ErrorMessageUBO>
        )}
      </OwnerInputField>
      <OwnerInputField>
        <Typography style={{ fontSize: '16px' }} fontWeight={FontWeights.Bold}>
          {' '}
          {translate('Invoices.OwnershipAmount')}{' '}
        </Typography>
        <DropDown
          name="ownerAmount"
          value={amount}
          disabled={false}
          dropDownValues={[
            { label: '25-50%', value: OwnerAmountEnum.TwentyFiveTo50 },
            { label: '50-75%', value: OwnerAmountEnum.FiftyTo75 },
            { label: '75-100%', value: OwnerAmountEnum.SeventyFiveTo100 },
            { label: '100%', value: OwnerAmountEnum.HoundredPercent },
          ]}
          onChange={onAmountChanged}
          validationType="OwnerType"
        />
        {amountError && (
          <ErrorMessageUBO>
            <ErrorMessageIcon>
              <StyledIcon color="#EB4C6A" icon={SvgSrc.WarningCircle} />
            </ErrorMessageIcon>
            <ErrorMessageText>{amountError}</ErrorMessageText>
          </ErrorMessageUBO>
        )}
      </OwnerInputField>
      <TryAgainContainer>
        <DoneButton onClick={saveOwner}>{translate('Save')}</DoneButton>
      </TryAgainContainer>
    </div>
  );
};

export default Owner;
